import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  lazy,
  Suspense,
  type ReactNode,
  type MouseEvent,
} from "react";
import { useKernel } from "@/kernel/useKernel";
import { useTheme } from "@/theme/ThemeContext";
import Editor from "@/components/Editor";
import Console from "@/components/Console";
import Button from "@/components/Button";
import Icon, { type IconName } from "@/components/Icon";
import { useConfirm } from "@/components/ConfirmDialog";
import { downloadText } from "@/utils/download";
import type { TutorTrace } from "@/kernel/tutorTrace";

// Visualiseur (PythonTutor maison) chargé à la demande — gros chunk hors bundle initial.
const VisualizerOverlay = lazy(() => import("@/components/visualizer/VisualizerOverlay"));

const CONSOLE_MIN = 90;   // px
const CONSOLE_MAX = 600;  // px
const CONSOLE_DEFAULT = 220;

interface Props {
  code: string;
  onChange: (code: string) => void;
  /** Nom du fichier proposé au téléchargement (.py). */
  downloadFilename?: string;
  /** Boutons spécifiques à la page (ex. Valider / Étape suivante), rendus dans la barre. */
  extraActions?: ReactNode;
  /** Scaffold prof (caché à l'élève) composé autour du code à chaque Exécuter.
   *  Non fourni dans le Playground (exec brut). Cf. plan gamelib P0/P1.5. */
  scaffold?: { pre: string; post: string; pack?: { name: string; files: Record<string, string> } };
}

/**
 * Atelier de code générique : éditeur Monaco + console redimensionnable + barre
 * d'actions (Exécuter/Arrêter, Effacer, Télécharger, Visualiser, taille du texte,
 * bascule console, menu « Plus » responsive) + overlay de visualisation.
 *
 * Mutualisé entre la page d'exercice et l'environnement de dev. Les actions propres
 * à une page (validation…) passent par le slot `extraActions`.
 */
export default function CodeWorkspace({ code, onChange, downloadFilename = "script.py", extraActions, scaffold }: Props) {
  const kernel    = useKernel();
  const { theme } = useTheme();
  const confirm   = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Barre d'actions responsive : sous le seuil de débordement, les actions secondaires
  // (Effacer, Télécharger, Visualiser) passent dans un menu « Plus » pour que les
  // boutons essentiels (Exécuter/Arrêter + extraActions) restent toujours visibles.
  const actionBarRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const compactRef = useRef(compact);
  compactRef.current = compact; // miroir synchrone, lu par measureBar()
  const fullWidthRef = useRef(0);

  const measureBar = useCallback(() => {
    const el = actionBarRef.current;
    if (!el) return;
    if (!compactRef.current) {
      fullWidthRef.current = el.scrollWidth;
      if (el.scrollWidth > el.clientWidth + 1) setCompact(true);
    } else if (fullWidthRef.current && el.clientWidth >= fullWidthRef.current + 8) {
      setCompact(false); // assez de place pour tout réafficher (marge = hystérésis)
    }
  }, []);

  useLayoutEffect(() => { measureBar(); });
  useEffect(() => {
    const el = actionBarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measureBar);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureBar]);

  // Taille du texte de l'éditeur de code (zoom), ajustable.
  const [editorFontSize, setEditorFontSize] = useState(14);
  const bumpEditorFont = (d: number) => setEditorFontSize((s) => Math.max(10, Math.min(28, s + d)));

  // Console masquable + hauteur draggable.
  const [consoleOpen, setConsoleOpen]     = useState(true);
  const [consoleHeight, setConsoleHeight] = useState(CONSOLE_DEFAULT);
  const draggingV       = useRef(false);
  const dragStartY      = useRef(0);
  const dragStartHeight = useRef(CONSOLE_DEFAULT);

  const onConsoleDragStart = useCallback((e: MouseEvent) => {
    draggingV.current        = true;
    dragStartY.current       = e.clientY;
    dragStartHeight.current  = consoleHeight;
    document.body.style.cursor     = "row-resize";
    document.body.style.userSelect = "none";
  }, [consoleHeight]);

  useEffect(() => {
    const onMove = (e: globalThis.MouseEvent) => {
      if (!draggingV.current) return;
      const delta = dragStartY.current - e.clientY; // glisser vers le haut agrandit
      const next  = Math.max(CONSOLE_MIN, Math.min(CONSOLE_MAX, dragStartHeight.current + delta));
      setConsoleHeight(next);
    };
    const onUp = () => {
      draggingV.current = false;
      document.body.style.cursor     = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Trace de visualisation (overlay PythonTutor) — null = fermé.
  const [trace, setTrace]       = useState<TutorTrace | null>(null);
  const [vizError, setVizError] = useState<string | null>(null);

  const handleDownload = useCallback(() => {
    downloadText(downloadFilename, code);
  }, [downloadFilename, code]);

  // Import de fichiers : un .py remplace le contenu de l'éditeur (avec confirmation
  // si du code est déjà présent) ; tout autre fichier est écrit dans le FS Pyodide
  // pour pouvoir être ouvert par le programme (open("data.txt")).
  const handleFiles = useCallback(async (files: FileList) => {
    for (const file of Array.from(files)) {
      if (file.name.toLowerCase().endsWith(".py")) {
        const text = await file.text();
        if (code.trim().length > 0) {
          const ok = await confirm({
            title: "Remplacer le code ?",
            message: `Importer « ${file.name} » écrasera le contenu actuel de l'éditeur.`,
            confirmLabel: "Remplacer",
          });
          if (!ok) continue;
        }
        onChange(text);
      } else {
        kernel.putFile(file.name, await file.arrayBuffer());
      }
    }
  }, [code, confirm, onChange, kernel]);

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  // Visualisation pas-à-pas. La 1ʳᵉ trace est générée sans saisie. Si le code appelle
  // input(), elle se termine par un événement `raw_input` que le visualiseur affiche
  // sous forme de champ de saisie au moment où l'élève atteint l'appel ; il relance
  // alors la génération avec la file enrichie (cf. VisualizerOverlay.regenerate).
  const handleVisualize = useCallback(async () => {
    setVizError(null);
    try {
      setTrace(await kernel.visualize(code, []));
    } catch (err) {
      setVizError(err instanceof Error ? err.message : "Échec de la visualisation.");
    }
  }, [kernel, code]);

  return (
    <div style={styles.right}>
      {/* Sélecteur de fichier masqué, déclenché par « Importer » (barre + menu Plus). */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.length) handleFiles(e.target.files);
          e.target.value = ""; // permet de réimporter le même fichier
        }}
      />

      <div style={styles.editorArea}>
        <Editor
          value={code}
          onChange={onChange}
          theme={theme === "light" ? "vs" : "vs-dark"}
          fontSize={editorFontSize}
        />
      </div>

      {/* Poignée de redimensionnement vertical — au-dessus de la barre d'actions. */}
      {consoleOpen && (
        <div
          className="resize-handle"
          style={styles.splitterH}
          onMouseDown={onConsoleDragStart}
          title="Glisser pour redimensionner la console"
        >
          <div style={styles.splitterHGrip} />
        </div>
      )}

      {/* Barre d'actions — outils à gauche, actions de page (extraActions) à droite. */}
      <div ref={actionBarRef} style={styles.actionBar}>
        {kernel.running ? (
          <Button variant="danger" onClick={kernel.interrupt} title="Arrêter l'exécution">
            <Icon name="stop" size={13} />
            Arrêter
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              kernel.clearOutput();
              kernel.run(code, scaffold);
            }}
            disabled={!kernel.ready}
          >
            <Icon name="run" size={14} />
            Exécuter
          </Button>
        )}

        {/* Actions secondaires : inline tant qu'il y a la place ; sinon repliées
            dans le menu « Plus » à droite. */}
        {!compact && (
          <>
            <Button variant="ghost" onClick={kernel.clearOutput}>
              <Icon name="clear" size={14} />
              Effacer
            </Button>
            <Button variant="ghost" onClick={handleDownload} title="Télécharger ce script au format .py">
              <Icon name="download" size={14} />
              Télécharger .py
            </Button>
            <Button
              variant="ghost"
              onClick={openFilePicker}
              title="Importer un .py (charge l'éditeur) ou un fichier de données (open() dans le code)"
            >
              <Icon name="upload" size={14} />
              Importer
            </Button>
            <Button
              variant="ghost"
              onClick={handleVisualize}
              disabled={!kernel.ready || kernel.visualizing}
              title="Visualiser l'exécution pas à pas"
            >
              {kernel.visualizing ? <><span className="spin" /> Analyse…</> : "Visualiser"}
            </Button>
          </>
        )}

        {vizError && (
          <span style={{ ...styles.feedback, color: "var(--red)" }}>{vizError}</span>
        )}

        {extraActions}

        {/* Contrôles de vue : inline si la place suffit, sinon repliés (avec les
            actions secondaires) dans le menu « Plus ». */}
        {compact ? (
          <div style={styles.viewControls}>
            <OverflowMenu
              items={[
                { key: "clear", label: "Effacer", icon: "clear", onClick: kernel.clearOutput },
                { key: "download", label: "Télécharger .py", icon: "download", onClick: handleDownload, title: "Télécharger ce script au format .py" },
                { key: "import", label: "Importer un fichier", icon: "upload", onClick: openFilePicker, title: "Importer un .py ou un fichier de données" },
                {
                  key: "viz",
                  label: kernel.visualizing ? "Analyse…" : "Visualiser",
                  onClick: handleVisualize,
                  disabled: !kernel.ready || kernel.visualizing,
                  title: "Visualiser l'exécution pas à pas",
                },
                { key: "fontup", label: "Agrandir le texte du code", onClick: () => bumpEditorFont(1), disabled: editorFontSize >= 28, keepOpen: true },
                { key: "fontdown", label: "Réduire le texte du code", onClick: () => bumpEditorFont(-1), disabled: editorFontSize <= 10, keepOpen: true },
                { key: "console", label: consoleOpen ? "Masquer la console" : "Afficher la console", onClick: () => setConsoleOpen((o) => !o) },
              ]}
            />
          </div>
        ) : (
          <div style={styles.viewControls}>
            <div style={styles.zoomGroup} title="Taille du texte du code">
              <button style={styles.zoomBtn} onClick={() => bumpEditorFont(-1)} disabled={editorFontSize <= 10} aria-label="Réduire le texte du code">A−</button>
              <button style={styles.zoomBtn} onClick={() => bumpEditorFont(1)} disabled={editorFontSize >= 28} aria-label="Agrandir le texte du code">A+</button>
            </div>
            <button
              type="button"
              style={styles.consoleToggle}
              onClick={() => setConsoleOpen((o) => !o)}
              aria-expanded={consoleOpen}
              title={consoleOpen ? "Masquer la console" : "Afficher la console"}
            >
              <Icon name={consoleOpen ? "down" : "up"} size={14} />
              Console
            </button>
          </div>
        )}
      </div>

      {consoleOpen && (
        <div style={{ ...styles.consoleArea, height: consoleHeight }}>
          <Console
            output={kernel.output}
            displays={kernel.displays}
            running={kernel.running}
            inputRequest={kernel.inputRequest}
            onSubmitInput={kernel.submitInput}
          />
        </div>
      )}

      {trace && (
        <Suspense fallback={null}>
          <VisualizerOverlay
            initialTrace={trace}
            regenerate={(inputs) => kernel.visualize(trace.code, inputs)}
            onClose={() => setTrace(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

// ─── Menu « Plus » (débordement responsive de la barre d'actions) ──────────────

interface MenuItemDef {
  key: string;
  label: string;
  icon?: IconName;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  keepOpen?: boolean; // garde le menu ouvert (ex. +/- de taille, clics répétés)
}

function OverflowMenu({ items }: { items: MenuItemDef[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <style>{`.pl-menu-item:hover:not(:disabled){background:var(--bg-base);} .pl-menu-item:disabled{opacity:.5;cursor:default;}`}</style>
      <button
        type="button"
        style={styles.moreBtn}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Plus d'actions"
      >
        <Icon name="more" size={16} />
        Plus
      </button>
      {open && (
        <div style={styles.menu} role="menu">
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              className="pl-menu-item"
              style={styles.menuItem}
              role="menuitem"
              disabled={it.disabled}
              title={it.title}
              onClick={() => { if (!it.keepOpen) setOpen(false); it.onClick(); }}
            >
              {it.icon && <Icon name={it.icon} size={14} />}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
  },
  editorArea: { flex: 1, minHeight: 0 },
  viewControls: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  zoomGroup: { display: "inline-flex", alignItems: "center", gap: 4 },
  zoomBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-muted)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    padding: "4px 8px",
  },
  consoleToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-muted)",
    padding: "4px 10px",
    cursor: "pointer",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  actionBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    background: "var(--bg-bar)",
    borderTop: "1px solid var(--border-soft)",
    borderBottom: "1px solid var(--border-soft)",
    flexShrink: 0,
  },
  feedback: {
    fontSize: 13,
    fontWeight: 600,
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    flexShrink: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  moreBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    color: "var(--text-muted)",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  menu: {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    right: 0,
    minWidth: 190,
    background: "var(--bg-panel)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-1)",
    padding: 4,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    zIndex: 60,
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: "var(--radius-sm)",
    color: "var(--text)",
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  splitterH: {
    height: 11,
    flexShrink: 0,
    background: "var(--bg-bar)",
    borderTop: "1px solid var(--border-soft)",
    cursor: "row-resize",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  splitterHGrip: {
    width: 40,
    height: 3,
    borderRadius: 2,
    background: "var(--border)",
  },
  consoleArea: { flexShrink: 0, minHeight: 0 },
};
